import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { BedDouble, Lock, Pencil } from 'lucide-react';
import {
  useBookings,
  useCanEditBookingFinancials,
} from '../../../../hooks/accommodation';
import { useAppSelector } from '../../../../hooks/useRedux';
import { selectAccommodationProperty } from '../../../../store/accommodation';
import { EditRoomRatesModal } from './EditRoomRatesModal';

export const BookingRoomsCard: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const property = useAppSelector(selectAccommodationProperty);
  const { data: bookings = [] } = useBookings(property?.id);
  const booking = bookings.find((b) => b.id === bookingId) ?? null;
  const { canEdit, hasPermission } = useCanEditBookingFinancials(booking);
  const [isEditRatesOpen, setIsEditRatesOpen] = useState(false);

  if (!booking) return null;

  const hasRooms = Boolean(booking.booking_rooms?.length);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-3.5">
      <div className="flex items-center justify-between border-b border-gray-100 pb-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Rooms
        </h4>
        {canEdit && hasRooms ? (
          <button
            type="button"
            onClick={() => setIsEditRatesOpen(true)}
            className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:border-pos-primary/40 hover:text-pos-primary"
          >
            <Pencil size={12} />
            Edit rates
          </button>
        ) : null}
        {!canEdit && hasPermission && hasRooms ? (
          <span className="flex items-center gap-1 text-[11px] text-gray-400">
            <Lock size={11} />
            Rates locked after check-in
          </span>
        ) : null}
      </div>
      {!booking.booking_rooms || booking.booking_rooms.length === 0 ? (
        <p className="text-sm text-gray-400">No rooms assigned.</p>
      ) : (
        <div className="space-y-2">
          {booking.booking_rooms.map((br) => (
            <div
              key={br.id}
              className="flex items-center justify-between rounded-xl border border-gray-100 px-3.5 py-3 bg-slate-50/50 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <BedDouble size={15} className="text-pos-primary shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    Room {br.room?.room_number ?? '—'}
                  </p>
                  <p className="text-xs text-gray-400 font-medium mt-0.5">
                    {br.room?.room_type?.name ?? ''}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">
                  {booking.currency} {br.subtotal.toLocaleString()}
                </p>
                <p className="text-xs text-gray-400 font-medium mt-0.5">
                  {br.nights} night{br.nights !== 1 ? 's' : ''} ×{' '}
                  {booking.currency} {br.price_per_night.toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      <EditRoomRatesModal
        isOpen={isEditRatesOpen}
        booking={booking}
        onClose={() => setIsEditRatesOpen(false)}
      />
    </div>
  );
};
